import ProductCard from "@/components/ProductCard";
import type { ProductSummary } from "@/common/productSummary/types";
import { FormatSubType, FormatType } from "@/constants/tagular/main";
import { useLocale } from "next-intl";
import { getTranslatedProductType } from "./utils/getTranslatedProductType";
import { ProgramType } from "@/common/program/types";
import { getLevel } from "@/components/Search/utils/getLevel";
import getProductSummaryFlexibility from "@/lib/productSummary/helper/getProductSummaryFlexibility";

function skillLabelFromProduct(
  product: ProductSummary & {
    productBadges?: { label?: string; text?: string }[];
  },
): string | undefined {
  const badges = product.productBadges;
  if (badges?.length) {
    const s = badges
      .map((b) => b.label ?? b.text ?? "")
      .filter(Boolean)
      .join(", ");
    if (s) return s;
  }
  return product.skills?.length ? product.skills.join(", ") : undefined;
}

interface ProductCardListProps {
  products: ProductSummary[];
  skipViewEvents?: ProductSummary[];
  shouldScroll?: boolean;
  location?: string;
  cardFormatType?: FormatType;
  cardFormatSubType?: FormatSubType;
  className?: string;
  isMultiCourse?: boolean;
  tagular_variant?: string;
  container_class?: string | undefined;
  isEdAssist?: boolean;
}

function ProductCardList({
  products,
  location,
  cardFormatType,
  cardFormatSubType,
  className,
  shouldScroll = true,
  skipViewEvents = [],
  isMultiCourse = false,
  tagular_variant,
  container_class = undefined,
  isEdAssist = false,
}: ProductCardListProps) {
  const locale = useLocale();
  if (!products || products.length === 0) {
    return null;
  }

  const styles = `gap-4 py-4 ${isMultiCourse ? "flex flex-wrap md:flex-nowrap overflow-x-scroll" : shouldScroll ? "flex overflow-x-scroll" : "dynamic-grid flex-wrap"} ${className}`;
  const hideCount = [
    ProgramType.BACHELORS,
    ProgramType.MASTERS,
    ProgramType.DOCTORATE,
  ];

  return (
    <div className={container_class || styles}>
      {products.map((product, index) => {
        product.productType = product.productType ?? "course";
        const baseHref = `/${product.productSlug}`;
        const href = isEdAssist ? `${baseHref}?edassist=true` : baseHref;
        const flexibility = getProductSummaryFlexibility(product.flexibility);
        return (
          <ProductCard
            key={`${location}-${product.productUuid}`}
            name={product.productName}
            href={href}
            imageUrl={product.productImageUrl}
            type={getTranslatedProductType(product.productType, locale)}
            partnerKey={product.partnerKeys}
            partnerFullName={product.partnerName}
            partnerLogoUrl={product.partnerLogoUrl}
            location={location}
            index={index}
            subjects={product.attributes}
            formatType={cardFormatType}
            formatSubtype={cardFormatSubType}
            skipViewEvent={skipViewEvents?.includes(product)}
            objectId={product.objectID}
            productSource={product.productSource ?? undefined}
            product={product.productCategory}
            uuid={product.productUuid}
            parentTitle={product.productName}
            activeRunKey={product.activeRunKey}
            childCoursesCount={
              hideCount.includes(product.productType as ProgramType)
                ? undefined
                : (product.childCoursesCount ??
                  product.courseCount ??
                  undefined)
            }
            organizationLogoOverride={product.organizationLogoOverride}
            tagular_variant={tagular_variant}
            weeksToComplete={product.weeksToComplete ?? undefined}
            flexibility={flexibility ?? undefined}
            pacing={
              flexibility as "self_paced" | "instructor_paced" | undefined
            }
            isNew={product.isNew}
            level={getLevel(product.level || "") || undefined}
            isPartOfProgram={product.isPartOfProgram}
            skillLabel={skillLabelFromProduct(product)}
            showBadges={!!(product as any).productBadges?.length}
            badges={(product as any).productBadges}
            className="no-underline hover:no-underline"
          />
        );
      })}
    </div>
  );
}

export default ProductCardList;
